import { PageSubtitle } from "@components/common/page-subtitle";
import { PageTitle } from "@components/common/page-title";
import { UlmContainer } from "@components/ulm/ulm-container";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { CompleteAccountForm } from "@pages/complete-account/complete-account-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchAccountData,
  getSubtitle,
  getTitle,
  isAccountComplete,
} from "@pages/complete-account/complete-account-utils";
import {
  AccountData,
  PageState,
} from "@pages/complete-account/complete-account-types";
import { useSearchParams } from "react-router-dom";
import { QueryParams } from "@router/route-params";
import { isValidRedirectUrl } from "@utils/browser-utils";
import { UlmButton } from "@components/ulm/ulm-button";
import { useAppNavigation } from "@hooks/use-app-navigation";
import { runtimeConfig } from "@src/runtime-config";
import { faroCoreApiClient } from "@api/core-api/core-api-utils";
import { HttpUtils } from "@stellar/utils";

/**
 * Page that allows users to complete require account information
 * that sales/marketing need.
 */
export function CompleteAccount(): JSX.Element {
  const { navigateToLogin } = useAppNavigation();
  const { handleError } = useErrorHandlerContext();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get(QueryParams.redirectUrl);
  const [accountData, setAccountData] = useState<AccountData>();
  const [pageState, setPageState] = useState<PageState>("loading");
  const [isUpdatingAccountData, setIsUpdatingAccountData] =
    useState<boolean>(false);

  const validRedirectUrl = useMemo(() => {
    return redirectUrl && isValidRedirectUrl(redirectUrl)
      ? redirectUrl
      : runtimeConfig.urls.sphereDashboardUrl;
  }, [redirectUrl]);

  // Fetch logged-in user and get the required account information
  useEffect(() => {
    async function fetchLoggedInUser(): Promise<void> {
      try {
        const account = await fetchAccountData();
        setAccountData(account);
        setPageState(
          isAccountComplete(account) ? "loadedComplete" : "loadedIncomplete"
        );
      } catch (error) {
        // The Core API returns an error with status code 401 if the user is not authenticated.
        // If the error is because the user is logged out then set the state as logged out.
        // If the error is for any other reason the display the error and log it to Sentry
        const errorStatus = HttpUtils.getErrorStatus(error);
        const isLoggedOutError =
          errorStatus !== undefined &&
          errorStatus === HttpUtils.HTTP_ERROR_CODE.Unauthorized;
        if (isLoggedOutError) {
          setPageState("loggedOut");
        } else {
          handleError({
            id: `fetchAccountData-${Date.now().toString()}`,
            title:
              "Failed to load the account data. Please reload the page and try again.",
            error,
            uiType: "page",
          });
        }
      }
    }

    void fetchLoggedInUser();
  }, [handleError]);

  // Updates the account information
  const updateAccountData = useCallback(
    async (accountData: AccountData): Promise<void> => {
      setIsUpdatingAccountData(true);

      // TODO: There are some fields in AccountData type that are not being used anymore, e.g. JobTitle.
      // We can explore removing those fields from the type and the form in a future iteration. https://faro01.atlassian.net/browse/ST-4579
      try {
        await faroCoreApiClient.users.updateUserProfile({
          firstName: accountData.name,
          lastName: accountData.lastName,
        });
        setPageState("updated");
      } catch (error) {
        handleError({
          id: `updateAccountData-${Date.now().toString()}`,
          title: "Failed to update the account data. Please try again.",
          error,
          uiType: "toast",
        });
      }

      setIsUpdatingAccountData(false);
    },
    [handleError]
  );

  return (
    <UlmContainer>
      <PageTitle>{getTitle(pageState)}</PageTitle>
      <PageSubtitle>{getSubtitle(pageState)}</PageSubtitle>

      {/* Show "Complete account" form when account data has been fetched and is missing required data */}
      {accountData && pageState === "loadedIncomplete" && (
        <CompleteAccountForm
          accountData={accountData}
          isLoading={isUpdatingAccountData}
          updateAccountData={updateAccountData}
        />
      )}

      {/* Show the button to redirect when the account data is complete */}
      {(pageState === "loadedComplete" || pageState === "updated") && (
        <UlmButton
          onClick={() => window.location.assign(validRedirectUrl)}
          sx={{
            marginTop: "20px",
          }}
        >
          Continue
        </UlmButton>
      )}

      {/* Show button to login if the user is logged out */}
      {pageState === "loggedOut" && (
        <UlmButton
          onClick={navigateToLogin}
          sx={{
            marginTop: "20px",
          }}
        >
          Login
        </UlmButton>
      )}
    </UlmContainer>
  );
}
